import { useApp } from '../context/AppContext';
import { Card, SectionLabel, StatCard } from '../components/UI';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line } from 'recharts';
import { TrendingUp } from 'lucide-react';

const COLORS = {
  sent: '#f5a623',
  replied: '#4d9fff',
  closed: '#00e5a0',
  skip: '#3a3a4a',
};

export default function AnalyticsPage() {
  const { pitches, stats } = useApp();

  // Pie chart data
  const pieData = [
    { name: 'Closed', value: stats.closed, color: COLORS.closed },
    { name: 'Replied', value: stats.replied, color: COLORS.replied },
    { name: 'Sent', value: stats.sent, color: COLORS.sent },
    { name: 'Skipped', value: stats.skipped, color: COLORS.skip },
  ].filter(d => d.value > 0);

  // Scripts performance
  const scriptCounts = ['A','B','C','D','E','AI'].map(s => ({
    script: `Script ${s}`,
    sent: pitches.filter(p => p.script === s).length,
    closed: pitches.filter(p => p.script === s && p.status === 'closed').length,
  })).filter(s => s.sent > 0);

  // Platform performance
  const platforms = ['X DM','X Reply','Discord','Telegram','Email'];
  const platformData = platforms.map(pl => ({
    platform: pl.replace(' ', '\n'),
    sent: pitches.filter(p => p.platform === pl).length,
    closed: pitches.filter(p => p.platform === pl && p.status === 'closed').length,
  })).filter(p => p.sent > 0);

  // Timeline — pitches per hour today
  const hourMap = {};
  pitches.forEach(p => {
    const h = new Date(p.createdAt).getHours();
    hourMap[h] = (hourMap[h] || 0) + 1;
  });
  const timelineData = Object.keys(hourMap).sort((a,b) => a-b).map(h => ({
    hour: `${h}:00`,
    pitches: hourMap[h],
  }));

  const tooltipStyle = {
    contentStyle: { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12, fontFamily: 'var(--font-mono)' },
    labelStyle: { color: 'var(--text)' },
    itemStyle: { color: 'var(--text2)' },
  };

  if (pitches.length === 0) {
    return (
      <div>
        <div style={{ marginBottom: '2rem' }}>
          <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 28, fontWeight: 800, letterSpacing: '-0.03em', marginBottom: 6 }}>Analytics</h1>
          <p style={{ color: 'var(--text2)', fontSize: 14 }}>Your performance data will appear here once you start logging pitches.</p>
        </div>
        <Card>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '4rem 2rem', gap: 14 }}>
            <TrendingUp size={40} color="var(--text3)" />
            <div style={{ fontSize: 15, fontWeight: 500, color: 'var(--text2)' }}>No data yet</div>
            <div style={{ fontSize: 13, color: 'var(--text3)', textAlign: 'center', maxWidth: 300, lineHeight: 1.6 }}>
              Go to the Tracker tab, log your pitches, and come back here to see your stats.
            </div>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: '2rem' }}>
        <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 28, fontWeight: 800, letterSpacing: '-0.03em', marginBottom: 6 }}>Analytics</h1>
        <p style={{ color: 'var(--text2)', fontSize: 14 }}>What's working. What's not. Double down on what converts.</p>
      </div>

      {/* Top stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 10, marginBottom: 20 }}>
        <StatCard label="Total pitches" value={stats.total} />
        <StatCard label="Replies" value={stats.replied} color="var(--blue)" />
        <StatCard label="Closed" value={stats.closed} color="var(--accent)" />
        <StatCard label="Reply rate" value={`${stats.replyRate}%`} color={stats.replyRate >= 20 ? 'var(--accent)' : 'var(--amber)'} />
        <StatCard label="Close rate" value={`${stats.closeRate}%`} color={stats.closeRate >= 30 ? 'var(--accent)' : 'var(--amber)'} sub={`$${stats.earned} USDT earned`} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
        {/* Pie chart */}
        <Card>
          <SectionLabel>Pitch outcomes</SectionLabel>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={pieData} cx="50%" cy="50%" innerRadius={55} outerRadius={90} paddingAngle={3} dataKey="value">
                {pieData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
              </Pie>
              <Tooltip {...tooltipStyle} />
            </PieChart>
          </ResponsiveContainer>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, justifyContent: 'center', marginTop: 4 }}>
            {pieData.map(d => (
              <div key={d.name} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text2)' }}>
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: d.color }} />
                {d.name} ({d.value})
              </div>
            ))}
          </div>
        </Card>

        {/* Script performance */}
        {scriptCounts.length > 0 && (
          <Card>
            <SectionLabel>Script performance</SectionLabel>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={scriptCounts} barGap={4}>
                <XAxis dataKey="script" tick={{ fontSize: 11, fill: 'var(--text3)', fontFamily: 'var(--font-mono)' }} axisLine={false} tickLine={false} />
                <YAxis hide />
                <Tooltip {...tooltipStyle} />
                <Bar dataKey="sent" fill={COLORS.sent} radius={[4,4,0,0]} name="Sent" />
                <Bar dataKey="closed" fill={COLORS.closed} radius={[4,4,0,0]} name="Closed" />
              </BarChart>
            </ResponsiveContainer>
            <div style={{ display: 'flex', gap: 14, justifyContent: 'center', marginTop: 4 }}>
              {[{k:'Sent',c:COLORS.sent},{k:'Closed',c:COLORS.closed}].map(l => (
                <div key={l.k} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text2)' }}>
                  <div style={{ width: 8, height: 8, borderRadius: 2, background: l.c }} />
                  {l.k}
                </div>
              ))}
            </div>
          </Card>
        )}
      </div>

      {/* Platform + timeline */}
      <div style={{ display: 'grid', gridTemplateColumns: platformData.length > 0 && timelineData.length > 1 ? '1fr 1fr' : '1fr', gap: 16 }}>
        {platformData.length > 0 && (
          <Card>
            <SectionLabel>Best platform</SectionLabel>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={platformData} layout="vertical">
                <XAxis type="number" hide />
                <YAxis dataKey="platform" type="category" tick={{ fontSize: 11, fill: 'var(--text3)', fontFamily: 'var(--font-mono)' }} axisLine={false} tickLine={false} width={70} />
                <Tooltip {...tooltipStyle} />
                <Bar dataKey="sent" fill={COLORS.sent} radius={[0,4,4,0]} name="Sent" />
                <Bar dataKey="closed" fill={COLORS.closed} radius={[0,4,4,0]} name="Closed" />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        )}

        {timelineData.length > 1 && (
          <Card>
            <SectionLabel>Pitch activity over time</SectionLabel>
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={timelineData}>
                <XAxis dataKey="hour" tick={{ fontSize: 11, fill: 'var(--text3)', fontFamily: 'var(--font-mono)' }} axisLine={false} tickLine={false} />
                <YAxis hide />
                <Tooltip {...tooltipStyle} />
                <Line type="monotone" dataKey="pitches" stroke="var(--accent)" strokeWidth={2} dot={{ fill: 'var(--accent)', r: 4 }} name="Pitches" />
              </LineChart>
            </ResponsiveContainer>
          </Card>
        )}
      </div>
    </div>
  );
}
